import { UiMessage } from "@intelligems/ig-types/schemas/ai/ui-message";

import {
  applyToTextParts,
  stripThinking,
} from "@src/features/ai/message-transform/stripThinking";

/**
 * View transform for the modifications-chat (Viv) stream. Hides model-only
 * content from the merchant, by role:
 * - assistant: strip the private `<thinking>` scratchpad.
 * - user: strip the `<instruction>`/`<context>` system blocks a semi-synthetic
 *   message carries for the model (a visible prompt plus a hidden guide).
 * A text part that collapses to empty is dropped so it leaves no bubble.
 * Returns a converted copy — raw chat state is never mutated.
 */
export function transformVivMessage(message: UiMessage): UiMessage {
  if (message.role === "assistant") {
    return applyToTextParts(message, stripThinking);
  }
  if (message.role === "user") {
    return applyToTextParts(message, stripSystemBlocks);
  }
  return message;
}

// System blocks a user message carries for the model only — `<instruction>` /
// `<context>`, documented in the chat system prompt's `<system-blocks>`
// section. They ride in the message text verbatim to the model but must be
// hidden from the merchant. `\1` keeps each close tag matched to its open tag.
const SYSTEM_BLOCK_TAGS = ["instruction", "context"] as const;

const SYSTEM_BLOCK_RE = new RegExp(
  `\\s*<(${SYSTEM_BLOCK_TAGS.join("|")})>[\\s\\S]*?</\\1>`,
  "g"
);

/**
 * Strip system blocks from a user message for display, leaving only the
 * merchant-visible text. Trimmed, so a message that is nothing but system
 * blocks collapses to `""` (no bubble). The write side — attaching a hidden
 * `<instruction>` guide — lives in the `sendIdea` thunk.
 */
export function stripSystemBlocks(text: string): string {
  return text.replace(SYSTEM_BLOCK_RE, "").trim();
}
